"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { gsap } from "gsap";

// All dimensions in rem (scale with viewport fluid font-size: 8px @ 1920px)
const DEFAULT_CARD_WIDTH_REM = 60.25;
const DEFAULT_ANGLE_STEP_DEG = 20;
const DEFAULT_CARD_GAP_REM = 16;

interface CircleSliderProps {
  count: number;
  cardWidthRem?: number;
  angleStepDeg?: number;
  cardGapRem?: number;
  className?: string;
  /** Controlled active index — animate to this item when it changes */
  externalIndex?: number;
  /** Called whenever the active index changes (drag or external navigation) */
  onActiveChange?: (index: number) => void;
  children: (index: number, isActive: boolean) => React.ReactNode;
}

export function CircleSlider({
  count,
  cardWidthRem = DEFAULT_CARD_WIDTH_REM,
  angleStepDeg = DEFAULT_ANGLE_STEP_DEG,
  cardGapRem = DEFAULT_CARD_GAP_REM,
  className,
  externalIndex,
  onActiveChange,
  children,
}: CircleSliderProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ticksRef = useRef<SVGGElement>(null);
  const geom = useRef({ cx: 0, cy: 0, R: 0, cw: 0, cardHeights: [] as number[] });
  const prog = useRef(0);
  const animRef = useRef<gsap.core.Tween | null>(null);
  const isDragging = useRef(false);
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;
  const [activeIndex, setActiveIndex] = useState(0);

  const place = useCallback(
    (p: number) => {
      const { cx, cy, R, cw, cardHeights } = geom.current;
      if (!R || !cw) return;
      const arcStep = angleStepDeg * (Math.PI / 180);
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const CH = cardHeights[i] || cw;
        const raw = (((p * count - i) % count) + count) % count;
        const centered = raw > count / 2 ? raw - count : raw;
        const angle = -centered * arcStep - Math.PI / 2;
        const rotation = -centered * angleStepDeg;
        const x = cx + R * Math.cos(angle) - cw / 2;
        const y = cy + R * Math.sin(angle) - CH / 2;
        card.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) rotate(${rotation.toFixed(2)}deg)`;
      });
    },
    [count, angleStepDeg],
  );

  const goTo = useCallback(
    (target: number, idx: number) => {
      animRef.current?.kill();
      const obj = { v: prog.current };
      animRef.current = gsap.to(obj, {
        v: target,
        duration: 0.55,
        ease: "power2.out",
        onUpdate: () => {
          prog.current = obj.v;
          place(obj.v);
        },
        onComplete: () => setActiveIndex(idx),
      });
    },
    [place],
  );

  // Notify parent when active index changes
  useEffect(() => {
    onActiveChangeRef.current?.(activeIndex);
  }, [activeIndex]);

  // Navigate when externalIndex changes (skip if user is dragging)
  useEffect(() => {
    if (externalIndex === undefined || isDragging.current) return;
    const currentStep = Math.round(prog.current * count);
    const currentIdx = ((currentStep % count) + count) % count;
    if (currentIdx === externalIndex) return;
    const diff = externalIndex - currentIdx;
    const shortDiff = ((diff % count) + count) % count;
    const adjustedDiff = shortDiff > count / 2 ? shortDiff - count : shortDiff;
    goTo(currentStep / count + adjustedDiff / count, externalIndex);
  }, [externalIndex, count, goTo]);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer || !count) return;

    const pxPerStepRef = { v: cardWidthRem * 8 };

    const recalc = () => {
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const arcStep = angleStepDeg * (Math.PI / 180);
      const CW = cardWidthRem * rem;
      const cardHeights = cardRefs.current.map((card) => card?.offsetHeight || CW);
      const CH = cardHeights[0] ?? CW;

      const R = (CW + cardGapRem * rem) / Math.sin(arcStep);
      const W = outer.offsetWidth;
      const cx = W / 2;
      const cy = R + CH / 2;

      geom.current = { cx, cy, R, cw: CW, cardHeights };

      if (ticksRef.current) {
        const numTicks = 700;
        const tickH = 3 * rem;
        ticksRef.current.innerHTML = "";
        for (let t = 0; t < numTicks; t++) {
          const angle = (t / numTicks) * 2 * Math.PI;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
          line.setAttribute("x1", String(cx + R * cos));
          line.setAttribute("y1", String(cy + R * sin));
          line.setAttribute("x2", String(cx + (R - tickH) * cos));
          line.setAttribute("y2", String(cy + (R - tickH) * sin));
          line.setAttribute("stroke", "white");
          line.setAttribute("stroke-opacity", "0.15");
          line.setAttribute("stroke-width", "1");
          ticksRef.current.appendChild(line);
        }
      }

      const sideDrop = R * (1 - Math.cos(arcStep));
      outer.style.height = `${Math.ceil(sideDrop + CH + 4 * rem)}px`;

      pxPerStepRef.v = Math.min(CW, cardWidthRem * 8);

      place(prog.current);
    };

    recalc();

    const ro = new ResizeObserver(recalc);
    ro.observe(outer);
    cardRefs.current.forEach((card) => {
      if (card) ro.observe(card);
    });

    let startX = 0,
      startP = 0,
      down = false;

    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      animRef.current?.kill();
      startX = e.clientX;
      startP = prog.current;
      down = true;
      isDragging.current = true;
      // cursor hidden — handled by parent section
    };

    const onMove = (e: PointerEvent) => {
      if (!down) return;
      e.preventDefault();
      prog.current = startP - (e.clientX - startX) / pxPerStepRef.v / count;
      place(prog.current);
      const idx = ((Math.round(prog.current * count) % count) + count) % count;
      setActiveIndex(idx);
    };

    const onUp = () => {
      if (!down) return;
      down = false;
      isDragging.current = false;
      // cursor hidden — handled by parent section
      const unitStep = 1 / count;
      const snapped = Math.round(prog.current / unitStep) * unitStep;
      const idx = ((Math.round(prog.current * count) % count) + count) % count;
      goTo(snapped, idx);
    };

    outer.addEventListener("pointerdown", onDown, { passive: false });
    document.addEventListener("pointermove", onMove, { passive: false });
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      ro.disconnect();
      outer.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, [count, cardWidthRem, angleStepDeg, cardGapRem, place, goTo]);

  return (
    <div
      ref={outerRef}
      className={cn("w-full relative overflow-hidden", className)}
      style={{ touchAction: "none" }}>
      {/* Track ticks */}
      <svg
        className="absolute inset-0 w-full pointer-events-none"
        style={{ height: "100%", overflow: "visible" }}>
        <g ref={ticksRef} />
      </svg>

      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          ref={(el) => {
            cardRefs.current[i] = el;
          }}
          className="absolute left-0 select-none top-[4rem]"
          style={{ width: `${cardWidthRem}rem`, transform: "translate3d(0, 0, 0)", willChange: "transform" }}>
          {children(i, i === activeIndex)}
        </div>
      ))}

      {/* Fade bottom edge */}
    </div>
  );
}
